import React from 'react'
import packageJson from '../../package.json'

// Get build date from environment variable or use current date as fallback
const getBuildDate = () => {
  if (process.env.REACT_APP_BUILD_DATE) {
    try {
      const date = new Date(process.env.REACT_APP_BUILD_DATE)
      // Check if date is valid
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      }
    } catch (error) {
      console.warn('Invalid date format in REACT_APP_BUILD_DATE:', process.env.REACT_APP_BUILD_DATE)
    }
  }
  
  // Fallback to current date formatted nicely
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const Help = () => {
  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">Help & Support</h1>
          <p className="text-gray-500">Get assistance with Green Farm Aid</p>
        </div>

        {/* Help Sections */}
        <div className="space-y-6">
          {/* Contact Support */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-start">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-4 mt-1">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">Contact Support</h3>
                <p className="text-gray-600 mb-3">
                  Need help? Our support team is here to assist you with any questions or issues.
                </p>
                <button className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send Message
                </button>
              </div>
            </div>
          </div>

          {/* Quick Start Guide */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-start">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4 mt-1">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">Quick Start Guide</h3>
                <p className="text-gray-600 mb-3">
                  New to Green Farm Aid? Learn the basics and get started quickly.
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Setting up your farm profile</li>
                  <li>• Managing workers and groups</li>
                  <li>• Recording agricultural activities</li>
                  <li>• Viewing dashboards and reports</li>
                </ul>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-start">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-4 mt-1">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">Frequently Asked Questions</h3>
                <p className="text-gray-600 mb-3">
                  Find answers to common questions about using the platform.
                </p>
                <div className="text-sm text-gray-600 space-y-2">
                  <div className="border-l-2 border-gray-200 pl-3">
                    <p className="font-medium">How do I add a new worker?</p>
                    <p className="text-gray-500">Go to Management → Workers and click "Add Worker"</p>
                  </div>
                  <div className="border-l-2 border-gray-200 pl-3">
                    <p className="font-medium">How do I view activity reports?</p>
                    <p className="text-gray-500">Visit the Dashboard or Reports section for detailed analytics</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* System Info */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-start">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-4 mt-1">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">System Information</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><span className="font-medium">Version:</span> {packageJson.version}</p>
                  <p><span className="font-medium">Last Updated:</span> {getBuildDate()}</p>
                  <p><span className="font-medium">Status:</span> 
                    <span className="ml-1 inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                      Online
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Help
